// Browser-side client for Deriv's NEW API (developers.deriv.com).
// OAuth tokens from auth.deriv.com only work here — NOT on the legacy ws.derivws.com `authorize`.
//   REST: GET  /trading/v1/options/accounts            → every account + balance + demo/real
//   REST: POST /trading/v1/options/accounts/{id}/otp   → one-time WS URL (no authorize message)
const DERIV_REST = "https://api.derivws.com";

async function _rest(method, path, token) {
  const r = await fetch(`${DERIV_REST}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (r.status === 401) throw new Error("InvalidToken");
  if (!r.ok) throw new Error(`Deriv REST ${r.status}`);
  return r.json();
}

export function normaliseAccount(a, token = "") {
  const demo = a.account_type === "demo" || !!a.is_virtual;
  return {
    loginid: a.account_id || a.loginid,
    account_id: a.account_id || a.loginid,
    token,
    currency: a.currency || "USD",
    account_type: demo ? "demo" : "real",
    is_virtual: demo,
    balance: a.balance,
    status: a.status,
  };
}

export function pickAccount(accounts, preferredId) {
  if (!accounts.length) return null;
  const pref = accounts.find((a) => a.account_id === preferredId);
  if (pref) return pref;
  const live = accounts.filter((a) => a.status !== "inactive");
  const pool = live.length ? live : accounts;
  return pool.find((a) => a.account_type === "real") || pool[0];
}

// Deriv's OTP URL (/ws/demo vs /ws/real) is the source of truth for Demo vs Real — cached per account.
async function _verifiedKind(token, accountId, listed) {
  const key = `naevo_kind_${accountId}`;
  const cached = localStorage.getItem(key);
  if (cached) return cached;
  try {
    const url = await fetchOtpUrl(token, accountId);
    const kind = url.includes("/ws/demo") ? "demo" : url.includes("/ws/real") ? "real" : listed;
    localStorage.setItem(key, kind);
    return kind;
  } catch {
    return listed;
  }
}

export async function fetchAccounts(token) {
  const j = await _rest("GET", "/trading/v1/options/accounts", token);
  const list = (j.data || []).filter((a) => a.account_id).map((a) => normaliseAccount(a, token));
  for (const a of list) {
    const kind = await _verifiedKind(token, a.account_id, a.account_type);
    a.account_type = kind;
    a.is_virtual = kind === "demo";
  }
  return list;
}

export async function fetchOtpUrl(token, accountId) {
  const j = await _rest("POST", `/trading/v1/options/accounts/${accountId}/otp`, token);
  if (!j?.data?.url) throw new Error("Deriv OTP URL missing");
  return j.data.url;
}

// Summary for the dashboard: active account + balances for all accounts.
export function summarise(accounts, preferredId) {
  const active = pickAccount(accounts, preferredId);
  const all_balances = {};
  for (const a of accounts) all_balances[a.account_id] = { balance: a.balance, currency: a.currency };
  return {
    loginid: active?.account_id || "",
    currency: active?.currency || "USD",
    balance: { balance: active?.balance ?? null, currency: active?.currency || "USD", loginid: active?.account_id },
    accounts,
    all_balances,
    is_virtual: !!active?.is_virtual,
  };
}

export async function fetchAccountSummary(token, preferredId) {
  return summarise(await fetchAccounts(token), preferredId);
}

export function persistSummary(summary, token) {
  localStorage.setItem("naevo_token", token);
  localStorage.setItem("naevo_loginid", summary.loginid);
  localStorage.setItem("naevo_active_account", summary.loginid);
  localStorage.setItem("naevo_currency", summary.currency || "USD");
  localStorage.setItem("naevo_account_type", summary.is_virtual ? "demo" : "real");
  localStorage.setItem("naevo_accounts", JSON.stringify(summary.accounts.map((a) => ({ ...a, token }))));
  if (summary.balance?.balance != null) localStorage.setItem("naevo_balance", Number(summary.balance.balance).toFixed(2));
  window.dispatchEvent(new Event("naevo-session-change"));
}

// Live balance stream on an OTP-authenticated socket. Returns an unsubscribe/close function.
export function subscribeBalance(token, accountId, onUpdate) {
  let ws = null;
  let closed = false;
  fetchOtpUrl(token, accountId).then((url) => {
    if (closed) return;
    ws = new WebSocket(url);
    ws.onopen = () => ws.send(JSON.stringify({ balance: 1, subscribe: 1 }));
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.msg_type === "balance" && msg.balance) onUpdate(msg.balance);
      } catch {}
    };
  }).catch(() => {});
  return () => {
    closed = true;
    try { ws && ws.close(); } catch {}
  };
}

// ---------- Public market data (no auth) — streams straight from Deriv ----------
const DERIV_PUBLIC_WS = "wss://api.derivws.com/trading/v1/options/ws/public";

function _publicStream(request, onMessage) {
  let ws = null;
  let closed = false;
  let retry = null;
  const open = () => {
    if (closed) return;
    ws = new WebSocket(DERIV_PUBLIC_WS);
    ws.onopen = () => ws.send(JSON.stringify(request));
    ws.onmessage = (e) => { try { onMessage(JSON.parse(e.data)); } catch {} };
    ws.onclose = () => { if (!closed) retry = setTimeout(open, 2000); };
  };
  open();
  return () => { closed = true; clearTimeout(retry); try { ws && ws.close(); } catch {} };
}

// onHistory(candles[{time,open,high,low,close}]), onBar(candle) for the live bar
export function streamCandles(symbol, granularity, onHistory, onBar, onError) {
  return _publicStream(
    { ticks_history: symbol, end: "latest", count: 500, style: "candles", granularity, subscribe: 1 },
    (m) => {
      if (m.error) return onError && onError(m.error);
      if (m.msg_type === "candles") {
        onHistory((m.candles || []).map((c) => ({ time: c.epoch, open: +c.open, high: +c.high, low: +c.low, close: +c.close })));
      } else if (m.msg_type === "ohlc" && m.ohlc) {
        const o = m.ohlc;
        onBar({ time: +o.open_time, open: +o.open, high: +o.high, low: +o.low, close: +o.close });
      }
    },
  );
}

export function streamTicks(symbol, onHistory, onTick, onError) {
  return _publicStream(
    { ticks_history: symbol, end: "latest", count: 1000, style: "ticks", subscribe: 1 },
    (m) => {
      if (m.error) return onError && onError(m.error);
      if (m.msg_type === "history" && m.history) {
        onHistory(m.history.prices.map((p, i) => ({ time: +m.history.times[i], value: +p })));
      } else if (m.msg_type === "tick" && m.tick) {
        onTick({ time: +m.tick.epoch, value: +m.tick.quote });
      }
    },
  );
}
